import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllIngredients,
  getSingleIngredientById,
  createIngredient,
  updateIngredient,
  deleteIngredients,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/ingredients";

// Thunks

export const fetchAllIngredients = createAsyncThunk(
  "ingredients/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllIngredients(restaurant_id, menu_id);
    if (res.status === "success") return res.data.ingredients;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleIngredient = createAsyncThunk(
  "ingredients/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleIngredientById(id);
    if (res.status === "success") return res.data.ingredient;
    return rejectWithValue(res.message);
  }
);

export const createNewIngredient = createAsyncThunk(
  "ingredients/create",
  async (data, { rejectWithValue }) => {
    const res = await createIngredient(data);
    if (res.status === "success") return res.data.ingredient;
    return rejectWithValue(res.message);
  }
);

export const updateExistingIngredient = createAsyncThunk(
  "ingredients/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateIngredient(id, data);
    if (res.status === "success") return res.data.ingredient;
    return rejectWithValue(res.message);
  }
);

export const deleteIngredientById = createAsyncThunk(
  "ingredients/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteIngredients(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  ingredients: [],
  singleIngredient: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const ingredientSlice = createSlice({
  name: "ingredients",
  initialState,
  reducers: {
    clearIngredientMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllIngredients.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllIngredients.fulfilled, (state, action) => {
        state.getLoading = false;
        state.ingredients = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllIngredients.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleIngredient.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleIngredient.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleIngredient = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleIngredient.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewIngredient.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewIngredient.fulfilled, (state, action) => {
        state.createLoading = false;
        state.ingredients.push(action.payload);
        state.success = "Ingredient created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewIngredient.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingIngredient.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingIngredient.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.ingredients = state.ingredients.map((ingredient) =>
          ingredient.id === action.payload.id ? action.payload : ingredient
        );
        state.success = "Ingredient updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingIngredient.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteIngredientById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteIngredientById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.ingredients = state.ingredients.map((item) =>
          item.id === action.payload ? { ...item, status: "archive" } : item
        );
        state.success = "Ingredient deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteIngredientById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearIngredientMessages } = ingredientSlice.actions;
export default ingredientSlice.reducer;
